import ArrowBackIcon from '@material-design-icons/svg/filled/arrow_back.svg?react';
import ErrorIcon from '@material-design-icons/svg/filled/error.svg?react';
import FolderIcon from '@material-design-icons/svg/filled/folder.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import type {
  Dapp,
  Publisher as PublisherDto,
  Release as ReleaseDto,
} from '@src/dto';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePolling } from '../../hooks/usePolling';
import { useTRPC } from '../../hooks/useTRPC';
import {
  createAttestationPayload,
  getAttestationErrorMessage,
} from '../../utils/attestation';
import { captureAndReportError } from '../../utils/errorReporting';
import {
  fetchMetadataJson,
  isMetadataUnavailableError,
} from '../../utils/metadataFetch';
import { getMetadataSolanaDappStore } from '../../utils/nftMetadataHelpers';
import {
  getReleaseManagementPermissionMessage,
  getStoreSubmissionPermissionMessage,
} from '../../utils/publicationAccess';
import { getDappHomePath } from '../../utils/dappRoutes';
import { useDappContext } from '../DappLayout';
import { useApkDownloadUrl } from './hooks/useApkDownloadUrl';
import { ReleaseLoadedContent } from './ReleaseLoadedContent';

const NftMetadataFileSchema = z.object({
  purpose: z.string().optional(),
  uri: z.string().optional(),
});

const ReleaseNftMetadataSchema = z.object({
  image: z.string().optional(),
  animation_url: z.string().optional(),
  external_url: z.string().optional(),
  extensions: z
    .object({
      solana_dapp_store: z
        .object({
          files: z.array(NftMetadataFileSchema).optional(),
        })
        .optional(),
    })
    .optional(),
});

type ReleaseNftMetadata = z.infer<typeof ReleaseNftMetadataSchema>;
type ReleaseNftMetadataSolanaDappStore = NonNullable<
  NonNullable<ReleaseNftMetadata['extensions']>['solana_dapp_store']
>;

export function ReleaseDetailPage() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const wallet = useWallet();
  const { dappId, packageName } = useDappContext();
  const { releaseId } = useParams<{ releaseId: string }>();
  const dappRouteDapp = { id: dappId, packageName };
  const dappHomePath = getDappHomePath(dappRouteDapp);
  const [release, setRelease] = useState<ReleaseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMintModal, setShowMintModal] = useState(false);
  const [dapp, setDapp] = useState<Dapp | null>(null);
  const [publisher, setPublisher] = useState<PublisherDto | null>(null);
  const [arweaveApkUrl, setArweaveApkUrl] = useState<string | null>(null);
  const [nftMetadata, setNftMetadata] = useState<ReleaseNftMetadata | null>(
    null,
  );
  const loadReleaseRequestIdRef = useRef(0);
  const processingStatusRequestRef = useRef<Promise<void> | null>(null);
  const routeKey = `${dappId ?? ''}:${releaseId ?? ''}`;
  const currentRouteKeyRef = useRef(routeKey);
  currentRouteKeyRef.current = routeKey;

  const apkDownloadUrl = useApkDownloadUrl(release?.releaseFileUrl);
  const releaseManagementPermissionMessage =
    getReleaseManagementPermissionMessage(dapp);
  const storeSubmissionPermissionMessage =
    getStoreSubmissionPermissionMessage(dapp);
  const canManageReleaseArtifacts = !releaseManagementPermissionMessage;
  const canSubmitReleaseToStore = !storeSubmissionPermissionMessage;

  usePageTitle('Release Details | Publishing Portal');

  useEffect(() => {
    return () => {
      loadReleaseRequestIdRef.current += 1;
      processingStatusRequestRef.current = null;
      currentRouteKeyRef.current = '';
    };
  }, [routeKey]);

  useEffect(() => {
    if (releaseId) {
      loadRelease();
    }
  }, [releaseId]);

  useEffect(() => {
    let isCancelled = false;

    const loadNftMetadata = async () => {
      if (!release?.nftMetadataUri) {
        setNftMetadata(null);
        setArweaveApkUrl(null);
        return;
      }

      try {
        const metadata = await fetchMetadataJson(release.nftMetadataUri);
        if (isCancelled) {
          return;
        }

        const parseResult = ReleaseNftMetadataSchema.safeParse(metadata);
        if (!parseResult.success) {
          captureAndReportError(
            'Error parsing NFT metadata:',
            parseResult.error.message,
          );
          setNftMetadata(null);
          setArweaveApkUrl(null);
          return;
        }

        setNftMetadata(parseResult.data);

        const solanaDappStore =
          getMetadataSolanaDappStore<ReleaseNftMetadataSolanaDappStore>(
            parseResult.data,
          );
        const apkFile = solanaDappStore?.files?.find(
          (file) => file.purpose === 'install',
        );
        setArweaveApkUrl(apkFile?.uri ?? null);
      } catch (err) {
        if (isCancelled) {
          return;
        }
        if (isMetadataUnavailableError(err)) {
          console.warn('NFT metadata is not available', {
            metadataUri: release.nftMetadataUri,
            releaseId: release.id,
            error: err,
          });
        } else {
          captureAndReportError(err, {
            operation: 'Error fetching NFT metadata',
            metadataUri: release.nftMetadataUri,
            releaseId: release.id,
          });
        }
        setNftMetadata(null);
        setArweaveApkUrl(null);
      }
    };

    loadNftMetadata();

    return () => {
      isCancelled = true;
    };
  }, [release?.id, release?.nftMetadataUri]);

  // Check on-chain collection verification status and sync DB if needed
  useEffect(() => {
    let isCancelled = false;
    const requestRouteKey = `${dappId ?? ''}:${releaseId ?? ''}`;
    const isCurrentRequest = () =>
      !isCancelled && currentRouteKeyRef.current === requestRouteKey;

    const checkAndSyncCollectionStatus = async () => {
      if (
        dapp?.canManageReleases !== true ||
        !release?.nftMintAddress ||
        release.isCollectionVerified ||
        !releaseId
      ) {
        return;
      }

      try {
        const result = await trpc.release.checkOnChainCollectionStatus.query({
          releaseId,
        });
        if (!isCurrentRequest()) {
          return;
        }

        if (result._tag === 'Right' && result.right.isVerifiedOnChain) {
          await trpc.release.markReleaseCollectionAsVerified.mutate({
            releaseId,
          });
          if (!isCurrentRequest()) {
            return;
          }
          setRelease((prev) =>
            prev ? { ...prev, isCollectionVerified: true } : prev,
          );
          toast.info('Collection verification status synced from blockchain');
        }
      } catch (err) {
        // Don't show error to user, this is a background sync
        captureAndReportError(
          'Error checking on-chain collection status:',
          err,
        );
      }
    };

    checkAndSyncCollectionStatus();

    return () => {
      isCancelled = true;
    };
  }, [
    dapp?.canManageReleases,
    release?.nftMintAddress,
    release?.isCollectionVerified,
    releaseId,
  ]);

  const loadRelease = async () => {
    if (!releaseId || !dappId) return;

    const requestId = loadReleaseRequestIdRef.current + 1;
    loadReleaseRequestIdRef.current = requestId;
    const requestRouteKey = `${dappId}:${releaseId}`;
    const isCurrentRequest = () =>
      loadReleaseRequestIdRef.current === requestId &&
      currentRouteKeyRef.current === requestRouteKey;

    try {
      setLoading(true);

      const result = await trpc.release.getReleaseForDapp.query({
        dappId,
        releaseId,
      });
      if (!isCurrentRequest()) {
        return;
      }

      if (result._tag === 'Left') {
        setError(result.left.message);
        toast.error('Failed to load release');
      } else {
        setRelease(result.right);

        if (result.right.status === 'Processing') {
          void loadProcessingStatus({
            processingJobId: result.right.processingJobId,
            currentStatus: result.right.status,
          });
        }

        const dappResult = await trpc.getDapp.query({ dappId });
        if (!isCurrentRequest()) {
          return;
        }

        if (dappResult._tag === 'Right') {
          setDapp(dappResult.right);
        }

        const publisherResult =
          await trpc.getCurrentUserPublisher.query(undefined);
        if (!isCurrentRequest()) {
          return;
        }

        if (publisherResult._tag === 'Right') {
          setPublisher(publisherResult.right);
        }
      }
    } catch (err) {
      if (!isCurrentRequest()) {
        return;
      }

      setError('Failed to load release');
      captureAndReportError('Error loading release:', err);
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
      }
    }
  };

  const loadProcessingStatus = async ({
    processingJobId,
    currentStatus,
  }: {
    processingJobId?: string | null;
    currentStatus?: ReleaseDto['status'];
  } = {}) => {
    if (!releaseId) return;

    const requestRouteKey = `${dappId ?? ''}:${releaseId}`;
    if (currentRouteKeyRef.current !== requestRouteKey) {
      return;
    }

    if (processingStatusRequestRef.current) {
      return processingStatusRequestRef.current;
    }

    let request!: Promise<void>;
    request = (async () => {
      try {
        const result = await trpc.release.getReleaseProcessingStatus.query({
          releaseId,
          processingJobId: processingJobId ?? release?.processingJobId,
        });
        if (currentRouteKeyRef.current !== requestRouteKey) {
          return;
        }

        if (result._tag === 'Right') {
          if (result.right.status !== (currentStatus ?? release?.status)) {
            await loadRelease();
          }
        }
      } catch (err) {
        if (currentRouteKeyRef.current !== requestRouteKey) {
          return;
        }

        captureAndReportError('Error loading processing status:', err);
      } finally {
        if (processingStatusRequestRef.current === request) {
          processingStatusRequestRef.current = null;
        }
      }
    })();

    processingStatusRequestRef.current = request;
    return request;
  };

  usePolling(loadProcessingStatus, 2000, {
    enabled: release?.status === 'Processing',
    preventOverlap: true,
  });

  const handleMintSuccess = (mintAddress: string, txSignature: string) => {
    setRelease((prev) =>
      prev
        ? {
            ...prev,
            nftMintAddress: mintAddress,
            nftTransactionSignature: txSignature,
          }
        : prev,
    );

    setShowMintModal(false);

    toast.success('Release NFT minted successfully!', {
      description: `Transaction: ${txSignature.substring(0, 20)}...`,
    });
  };

  const handleVerificationSuccess = async () => {
    await loadRelease();

    if (!canSubmitReleaseToStore) {
      if (storeSubmissionPermissionMessage) {
        toast.info(storeSubmissionPermissionMessage, {
          duration: 6000,
        });
      }
      return;
    }

    if (release && release.submissionStatus === 'NotSubmitted') {
      let currentStep: 'attestation' | 'submit' = 'attestation';
      try {
        toast.info('Generating attestation for store submission...', {
          duration: 5000,
        });

        const attestationResult = await createAttestationPayload(trpc, wallet);

        toast.info('Submitting release to store...', { duration: 5000 });
        currentStep = 'submit';

        const submitToStoreResult = await trpc.release.submitToStore.mutate({
          releaseId: release.id,
          whatsNew: release.newInVersion || '',
          criticalUpdate: false,
          attestation: {
            payload: attestationResult.attestationPayload,
            requestUniqueId: attestationResult.requestUniqueId,
          },
        });

        if (
          submitToStoreResult._tag === 'Right' &&
          submitToStoreResult.right.success
        ) {
          toast.success(submitToStoreResult.right.message, {
            description: submitToStoreResult.right.hubspotTicketId
              ? `Ticket ID: ${submitToStoreResult.right.hubspotTicketId}`
              : undefined,
            duration: 8000,
          });
          await loadRelease();
        } else {
          toast.error('Failed to submit release to store', {
            description: 'Please try again or contact support',
          });
        }
      } catch (submitError) {
        captureAndReportError('Failed to submit to store:', submitError);
        if (currentStep === 'attestation') {
          toast.error('Failed to generate attestation', {
            description: getAttestationErrorMessage(submitError),
          });
        } else {
          toast.error('Failed to submit release to store', {
            description: 'Please try again or contact support',
          });
        }
      }
    }
  };

  if (loading || error || !release) {
    return (
      <ReleaseShell onBack={() => navigate(dappHomePath)}>
        {loading && <ReleaseSkeleton />}
        {!loading && error && (
          <EmptyState
            tone="error"
            icon={<ErrorIcon className="size-5 fill-red-400" />}
            title="Couldn't load release"
            description={error}
          />
        )}
        {!loading && !error && !release && (
          <EmptyState
            icon={<FolderIcon className="size-5 fill-neutral-400" />}
            title="Release not found"
            description="This release may have been removed or you may not have access."
          />
        )}
      </ReleaseShell>
    );
  }

  return (
    <ReleaseLoadedContent
      release={release}
      dapp={dapp}
      publisher={publisher}
      nftMetadata={nftMetadata}
      apkDownloadUrl={apkDownloadUrl}
      arweaveApkUrl={arweaveApkUrl}
      canManageReleaseArtifacts={canManageReleaseArtifacts}
      canSubmitReleaseToStore={canSubmitReleaseToStore}
      showMintModal={showMintModal}
      onBack={() => navigate(dappHomePath)}
      onReloadRelease={loadRelease}
      onMintClick={() => setShowMintModal(true)}
      onMintClose={() => setShowMintModal(false)}
      onMintSuccess={handleMintSuccess}
      onVerificationSuccess={handleVerificationSuccess}
    />
  );
}

interface ReleaseShellProps {
  onBack: () => void;
  children: ReactNode;
}

function ReleaseShell({ onBack, children }: ReleaseShellProps) {
  return (
    <div className="flex flex-col h-full bg-[#0f1011]">
      <header className="border-b border-white/5">
        <div className="px-8 py-6 max-w-5xl mx-auto w-full">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-xs text-neutral-500 hover:text-neutral-300 transition-colors -ml-1"
          >
            <ArrowBackIcon className="size-4 fill-current" />
            Back to releases
          </button>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto">
        <div className="p-8 max-w-5xl mx-auto">{children}</div>
      </div>
    </div>
  );
}

function ReleaseSkeleton() {
  return (
    <div aria-busy="true" aria-live="polite" className="animate-pulse">
      <span className="sr-only">Loading release…</span>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-10">
        <div className="min-w-0 flex-1 space-y-3">
          <div className="h-8 w-72 max-w-full rounded-md bg-white/[0.06]" />
          <div className="flex items-center gap-3">
            <div className="h-3 w-32 rounded-full bg-white/[0.04]" />
            <div className="size-3 rounded-full bg-white/[0.03]" />
            <div className="h-3 w-10 rounded-full bg-white/[0.04]" />
            <div className="size-3 rounded-full bg-white/[0.03]" />
            <div className="h-3 w-24 rounded-full bg-white/[0.04]" />
          </div>
        </div>
        <div className="h-7 w-24 rounded-full bg-white/[0.05]" />
      </div>

      <SkeletonSection>
        <div className="flex items-baseline justify-between gap-4 pb-5 border-b border-white/5">
          <div className="space-y-2 min-w-0 flex-1">
            <div className="h-3.5 w-64 max-w-full rounded bg-white/[0.06]" />
            <div className="h-3 w-32 rounded bg-white/[0.04]" />
          </div>
          <div className="h-3.5 w-16 rounded bg-white/[0.05]" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-5 pt-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonField key={i} />
          ))}
        </div>
      </SkeletonSection>

      <SkeletonSection>
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-y-1 sm:gap-x-6 py-2 items-center"
            >
              <div className="h-2.5 w-16 rounded bg-white/[0.05]" />
              <div className="h-3.5 w-full max-w-[260px] rounded bg-white/[0.04]" />
            </div>
          ))}
        </div>
      </SkeletonSection>

      <div className="rounded-2xl bg-white/[0.02] border border-white/5 px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-3 w-24 rounded bg-white/[0.06]" />
          <div className="h-3 w-6 rounded bg-white/[0.04]" />
        </div>
        <div className="size-3.5 rounded bg-white/[0.05]" />
      </div>
    </div>
  );
}

function SkeletonSection({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/5 mb-4">
      <div className="flex items-center justify-between gap-4 px-6 pt-5 pb-4">
        <div className="h-3 w-20 rounded bg-white/[0.06]" />
        <div className="h-6 w-24 rounded-full bg-white/[0.05]" />
      </div>
      <div className="px-6 pb-6">{children}</div>
    </div>
  );
}

function SkeletonField() {
  return (
    <div className="space-y-2">
      <div className="h-2.5 w-12 rounded bg-white/[0.05]" />
      <div className="h-3.5 w-24 rounded bg-white/[0.04]" />
    </div>
  );
}

interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  description: string;
  tone?: 'neutral' | 'error';
}

function EmptyState({
  icon,
  title,
  description,
  tone = 'neutral',
}: EmptyStateProps) {
  const accent =
    tone === 'error'
      ? 'bg-red-500/10 border-red-500/20'
      : 'bg-white/[0.04] border-white/10';
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/5 px-8 py-16 flex flex-col items-center text-center">
      <div
        className={`w-10 h-10 rounded-full border flex items-center justify-center mb-4 ${accent}`}
      >
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-white">{title}</h3>
      <p className="text-xs text-neutral-500 mt-1.5 max-w-sm">{description}</p>
    </div>
  );
}
